// Bosses. Quest-gated spawn: visit both of your faction's bonus venues,
// then your boss appears at your base, friendly to your team. Kill the
// enemy boss to drop 3 legendary weapons that only your team can loot.
//
// Transport-neutral so both party/town.ts and the SoloStub can drive it.

import type { Role } from '../net/protocol';
import type { PowerupEffect } from './powerups';

export type BossKind = 'redditMod' | 'gymfluencer';

export type LegendaryId =
  | 'death_note' | 'selfie_stick' | 'preworkout_shaker'
  | 'ban_hammer' | 'karma_farm'  | 'downvote_arrow';

export const BOSS_TUNING = {
  HP_MAX: 500,
  CONTACT_RADIUS: 2.4,
  CONTACT_DPS: 22,
  MOVE_SPEED: 3.2,
  AGGRO_RANGE: 14,
  REGEN_DELAY_MS: 3000,
  REGEN_PER_SEC: 18,
  HEAL_RADIUS: 3.0,
  HEAL_AMOUNT: 14,
  HEAL_COOLDOWN_MS: 500,
  SPAWN_INSET: 15,
  VENUE_RADIUS: 3.2,
  VENUE_UNLOCK_FIRST_SEC: 60,
  VENUE_UNLOCK_SECOND_SEC: 180,
  LEGENDARY_PICK_RADIUS: 1.5,
} as const;

export type Venue = {
  id: number;
  role: Role;
  x: number;
  z: number;
  name: string;
  icon: string;
};

// Positions are SHARED between teams per plan — same tile renders the
// Virgin venue for Virgin and the Chad venue for Chad. Both
// teams must visit both tiles, which forces them to converge mid-map.
// Venue positions sized to fit inside the SMALLEST themed arena
// (bedroom, ±18) so all 4 arenas contain the shrines. Any (mode,
// arena) combo renders them inside the playfield.
export const VENUES: ReadonlyArray<Venue> = [
  { id: 0, role: 'int', x: -13, z:  10, name: 'Manga Shop',    icon: '📚' },
  { id: 1, role: 'int', x:  13, z: -10, name: 'Internet Cafe', icon: '💻' },
  { id: 2, role: 'ext', x: -13, z:  10, name: 'The Mall',       icon: '🛍️' },
  { id: 3, role: 'ext', x:  13, z: -10, name: 'Nightclub',      icon: '🪩' },
];

const TEAM_VENUES: Record<Role, Venue[]> = {
  int: VENUES.filter((v) => v.role === 'int'),
  ext: VENUES.filter((v) => v.role === 'ext'),
};

// Drops grouped by the team that can pick them up (= enemy of boss's side).
export const LEGENDARY_DROPS: Record<Role, LegendaryId[]> = {
  int: ['death_note', 'selfie_stick', 'preworkout_shaker'],
  ext: ['ban_hammer',  'karma_farm',  'downvote_arrow'],
};

export const LEGENDARY_EFFECTS: Record<LegendaryId, PowerupEffect> = {
  death_note:        { damageMult: 1.35 },
  selfie_stick:      { ringOuterMult: 1.40 },
  preworkout_shaker: { damageMult: 1.25, speedMult: 0.95 },
  ban_hammer:        { circleRadiusMult: 1.80 },
  karma_farm:        { xpMult: 1.50 },
  downvote_arrow:    { damageMult: 1.30 },
};

export function legendaryName(id: LegendaryId): string {
  switch (id) {
    case 'death_note':        return 'Death Note';
    case 'selfie_stick':      return 'Extended Selfie Stick';
    case 'preworkout_shaker': return 'Pre-Workout Shaker';
    case 'ban_hammer':        return 'Ban Hammer';
    case 'karma_farm':        return 'Karma Farm';
    case 'downvote_arrow':    return 'Downvote Arrow';
  }
}

export function bossDisplayName(kind: BossKind): string {
  // Wire codes (`redditMod` / `gymfluencer`) intentionally kept for
  // protocol compat; display strings rebranded 2026-04-28 to DISCORD
  // MOD (Virgin) + LADY IN RED DRESS (Chad).
  return kind === 'redditMod' ? 'DISCORD MOD' : 'LADY IN RED DRESS';
}

export type BossSnap = {
  id: number;
  kind: BossKind;
  side: Role;
  x: number;
  z: number;
  hp: number;
  hpMax: number;
  targetId: string;
};

export type LegendarySnap = {
  id: number;
  x: number;
  z: number;
  weapon: LegendaryId;
  team: Role;
};

export type BossAttacker = {
  id: string;
  role: Role;
  x: number;
  z: number;
  hood: boolean;
  alive: boolean;
};

export type BossSpawnEvent = { kind: BossKind; side: Role; x: number; z: number };
export type BossKillEvent  = { kind: BossKind; lootTeam: Role; x: number; z: number };
export type BossEnrageEvent = { kind: BossKind; side: Role; x: number; z: number };
export type LegendaryPickupEvent = {
  pickerId: string;
  weapon: LegendaryId;
  x: number;
  z: number;
};
export type VenueVisitEvent = { playerId: string; venueId: number };

export type BossEvents = {
  spawned: BossSpawnEvent[];
  killed:  BossKillEvent[];
  enraged: BossEnrageEvent[];
  pickups: LegendaryPickupEvent[];
  visits:  VenueVisitEvent[];
};

type ServerBoss = {
  id: number;
  kind: BossKind;
  side: Role;
  x: number;
  z: number;
  hp: number;
  hpMax: number;
  alive: boolean;
  targetId: string;
  lastDamageMs: number;
  // C2 — enrage state. Once `hp <= hpMax/2` the boss flips to true and
  // stays enraged for the rest of its life. Damage + move-speed
  // multipliers apply only while enraged.
  enraged: boolean;
};

type ServerLegendary = {
  id: number;
  x: number;
  z: number;
  weapon: LegendaryId;
  team: Role;
};

function popCount(n: number): number {
  let c = 0;
  while (n) { c += n & 1; n >>>= 1; }
  return c;
}

export class BossManager {
  private bosses: ServerBoss[] = [];
  private legendaries: ServerLegendary[] = [];
  private visits = new Map<string, number>();
  private spawned: Record<Role, boolean> = { int: false, ext: false };
  private lastHealMs = new Map<string, number>();
  private nextId = 1;
  // Pending out-of-band kill events (debug/forceKillBoss). Drained
  // into the next tick's BossEvents so SoloStub broadcasts bossDeath
  // through the same path as natural deaths — fixes the cinematic +
  // legendary-loot pipeline for debug-driven kills.
  private pendingKilled: BossKillEvent[] = [];

  reset() {
    this.bosses = [];
    this.legendaries = [];
    this.pendingKilled.length = 0;
    this.visits.clear();
    this.spawned = { int: false, ext: false };
    this.lastHealMs.clear();
    this.nextId = 1;
  }

  resetPlayer(id: string) {
    this.visits.delete(id);
    this.lastHealMs.delete(id);
  }

  // Debug-only: spawn a boss instantly, bypassing the venue-quest gate.
  // Returns the spawned boss snapshot so callers can drive a cinematic.
  // No-op if a living boss already exists for that side.
  forceSpawn(kind: BossKind, side: Role, x: number, z: number, nowMs: number): BossSpawnEvent | null {
    if (this.hasBossForSide(side)) return null;
    const boss: ServerBoss = {
      id: this.nextId++,
      kind, side, x, z,
      hp: BOSS_TUNING.HP_MAX,
      hpMax: BOSS_TUNING.HP_MAX,
      alive: true,
      targetId: '',
      lastDamageMs: nowMs,
      enraged: false,
    };
    this.bosses.push(boss);
    this.spawned[side] = true;
    return { kind, side, x, z };
  }

  // Debug-only: kill the living boss for a side immediately, dropping
  // the legendary set just like a normal boss kill. Returns the kill
  // event so callers can render the toast/loot.
  forceKillBoss(side: Role): BossKillEvent | null {
    const boss = this.getLivingBoss(side);
    if (!boss) return null;
    boss.alive = false;
    boss.hp = 0;
    const lootTeam: Role = boss.side === 'int' ? 'ext' : 'int';
    const drops = LEGENDARY_DROPS[lootTeam];
    for (let i = 0; i < drops.length; i++) {
      const ang = (i / drops.length) * Math.PI * 2 + Math.PI / 6;
      this.legendaries.push({
        id: this.nextId++,
        x: boss.x + Math.cos(ang) * 2.0,
        z: boss.z + Math.sin(ang) * 2.0,
        weapon: drops[i],
        team: lootTeam,
      });
    }
    this.bosses = this.bosses.filter((b) => b.alive);
    const ev: BossKillEvent = { kind: boss.kind, lootTeam, x: boss.x, z: boss.z };
    // Queue for caller drain so the same downstream broadcast +
    // cinematic + loot path runs. Without this, a debug kill would
    // bypass the bossDeath wire entirely. Drained via
    // drainPendingKilled() — caller is responsible.
    this.pendingKilled.push(ev);
    return ev;
  }

  // Out-of-band kill drain. Used by SoloStub (and potentially other
  // hosts) to flush debug-driven kills regardless of mode gating —
  // bossMgr.tick is mode-gated to enableBoss modes, but a debug
  // forceKillBoss can fire from any state.
  drainPendingKilled(): BossKillEvent[] {
    if (this.pendingKilled.length === 0) return [];
    const out = this.pendingKilled.slice();
    this.pendingKilled.length = 0;
    return out;
  }

  // Debug-only: set the living boss HP for a side. Clamped to [0, hpMax].
  setBossHp(side: Role, hp: number): boolean {
    const boss = this.getLivingBoss(side);
    if (!boss) return false;
    boss.hp = Math.max(0, Math.min(boss.hpMax, hp));
    return true;
  }

  // Move venue-visit + heal-cooldown state from one id to another.
  // Used when a human "takes over" a bot on join — the human inherits
  // the bot's boss-quest progress so the match continues smoothly.
  transferVisits(fromId: string, toId: string): void {
    const mask = this.visits.get(fromId);
    if (mask !== undefined) {
      this.visits.set(toId, mask);
      this.visits.delete(fromId);
    }
    const heal = this.lastHealMs.get(fromId);
    if (heal !== undefined) {
      this.lastHealMs.set(toId, heal);
      this.lastHealMs.delete(fromId);
    }
  }

  getVisitMask(id: string): number {
    return this.visits.get(id) ?? 0;
  }

  hasBossForSide(side: Role): boolean {
    return this.bosses.some((b) => b.side === side && b.alive);
  }

  getLivingBoss(side: Role): ServerBoss | null {
    return this.bosses.find((b) => b.side === side && b.alive) ?? null;
  }

  // Apply damage dealt by an enemy player weapon. Caller is responsible
  // for checking range — this just decrements HP on the boss belonging
  // to the opposite team of the attacker.
  damageBoss(attackerRole: Role, amount: number, nowMs: number): number {
    if (amount <= 0) return 0;
    const targetSide: Role = attackerRole === 'int' ? 'ext' : 'int';
    const boss = this.getLivingBoss(targetSide);
    if (!boss) return 0;
    const dealt = Math.min(boss.hp, amount);
    boss.hp -= dealt;
    boss.lastDamageMs = nowMs;
    return dealt;
  }

  // E-to-heal near own boss. Returns true if a heal tick was applied.
  tryHealBoss(healerId: string, healerRole: Role, x: number, z: number, nowMs: number): boolean {
    const last = this.lastHealMs.get(healerId) ?? 0;
    if (nowMs - last < BOSS_TUNING.HEAL_COOLDOWN_MS) return false;
    const boss = this.getLivingBoss(healerRole);
    if (!boss) return false;
    const dx = boss.x - x;
    const dz = boss.z - z;
    const rr = BOSS_TUNING.HEAL_RADIUS + BOSS_TUNING.CONTACT_RADIUS;
    if (dx * dx + dz * dz > rr * rr) return false;
    if (boss.hp >= boss.hpMax) return false;
    boss.hp = Math.min(boss.hpMax, boss.hp + BOSS_TUNING.HEAL_AMOUNT);
    this.lastHealMs.set(healerId, nowMs);
    return true;
  }

  tick(
    dt: number,
    nowMs: number,
    elapsedMatchSec: number,
    attackers: ReadonlyArray<BossAttacker>,
    damagePlayer: (id: string, dmg: number) => void,
  ): BossEvents {
    const events: BossEvents = { spawned: [], killed: [], enraged: [], pickups: [], visits: [] };

    // 1) Venue visit tracking (time-gated)
    const venueRSq = BOSS_TUNING.VENUE_RADIUS * BOSS_TUNING.VENUE_RADIUS;
    for (const a of attackers) {
      if (!a.alive) continue;
      const teamV = TEAM_VENUES[a.role];
      let mask = this.visits.get(a.id) ?? 0;
      const orig = mask;
      for (let i = 0; i < teamV.length; i++) {
        const bit = 1 << i;
        if (mask & bit) continue;
        const already = popCount(mask);
        const gate = already === 0
          ? BOSS_TUNING.VENUE_UNLOCK_FIRST_SEC
          : BOSS_TUNING.VENUE_UNLOCK_SECOND_SEC;
        if (elapsedMatchSec < gate) continue;
        const v = teamV[i];
        const dx = a.x - v.x;
        const dz = a.z - v.z;
        if (dx * dx + dz * dz <= venueRSq) {
          mask |= bit;
          events.visits.push({ playerId: a.id, venueId: v.id });
        }
      }
      if (mask !== orig) this.visits.set(a.id, mask);
    }

    // 2) Boss spawn (once per team per match; team-collective mask)
    for (const side of ['int', 'ext'] as Role[]) {
      if (this.spawned[side]) continue;
      if (this.hasBossForSide(side)) continue;
      const fullMask = (1 << TEAM_VENUES[side].length) - 1;
      let teamMask = 0;
      for (const a of attackers) {
        if (a.role !== side) continue;
        teamMask |= this.visits.get(a.id) ?? 0;
      }
      if (teamMask !== fullMask) continue;
      const kind: BossKind = side === 'int' ? 'redditMod' : 'gymfluencer';
      const baseX = side === 'int'
        ? -85 + BOSS_TUNING.SPAWN_INSET
        :  85 - BOSS_TUNING.SPAWN_INSET;
      const boss: ServerBoss = {
        id: this.nextId++,
        kind,
        side,
        x: baseX,
        z: 0,
        hp: BOSS_TUNING.HP_MAX,
        hpMax: BOSS_TUNING.HP_MAX,
        alive: true,
        targetId: '',
        lastDamageMs: nowMs,
        enraged: false,
      };
      this.bosses.push(boss);
      this.spawned[side] = true;
      events.spawned.push({ kind, side, x: boss.x, z: boss.z });
    }

    // 3) Tick alive bosses (AI + contact damage + regen + death + enrage)
    for (const boss of this.bosses) {
      if (!boss.alive) continue;
      // Enrage gate — first crossing of 50% HP. One-way; re-healing
      // above 50% does NOT reset the flag (regen still ticks normally).
      if (!boss.enraged && boss.hp <= boss.hpMax * 0.5) {
        boss.enraged = true;
        events.enraged.push({ kind: boss.kind, side: boss.side, x: boss.x, z: boss.z });
      }
      const dpsMult   = boss.enraged ? 1.5 : 1;
      const speedMult = boss.enraged ? 1.3 : 1;
      let best: BossAttacker | null = null;
      let bestDist: number = BOSS_TUNING.AGGRO_RANGE;
      for (const a of attackers) {
        if (!a.alive) continue;
        if (a.role === boss.side) continue;
        if (a.hood) continue;
        const d = Math.hypot(a.x - boss.x, a.z - boss.z);
        if (d < bestDist) { bestDist = d; best = a; }
      }
      boss.targetId = best?.id ?? '';
      if (best) {
        const dx = best.x - boss.x;
        const dz = best.z - boss.z;
        const d = Math.hypot(dx, dz);
        if (d > BOSS_TUNING.CONTACT_RADIUS) {
          const step = Math.min(d - BOSS_TUNING.CONTACT_RADIUS, BOSS_TUNING.MOVE_SPEED * speedMult * dt);
          if (d > 0.001) {
            boss.x += (dx / d) * step;
            boss.z += (dz / d) * step;
          }
        } else {
          damagePlayer(best.id, BOSS_TUNING.CONTACT_DPS * dpsMult * dt);
        }
      }
      if (nowMs - boss.lastDamageMs >= BOSS_TUNING.REGEN_DELAY_MS && boss.hp < boss.hpMax) {
        boss.hp = Math.min(boss.hpMax, boss.hp + BOSS_TUNING.REGEN_PER_SEC * dt);
      }
      if (boss.hp <= 0) {
        boss.alive = false;
        boss.hp = 0;
        const lootTeam: Role = boss.side === 'int' ? 'ext' : 'int';
        const drops = LEGENDARY_DROPS[lootTeam];
        for (let i = 0; i < drops.length; i++) {
          const ang = (i / drops.length) * Math.PI * 2 + Math.PI / 6;
          this.legendaries.push({
            id: this.nextId++,
            x: boss.x + Math.cos(ang) * 2.0,
            z: boss.z + Math.sin(ang) * 2.0,
            weapon: drops[i],
            team: lootTeam,
          });
        }
        events.killed.push({ kind: boss.kind, lootTeam, x: boss.x, z: boss.z });
      }
    }
    this.bosses = this.bosses.filter((b) => b.alive);

    // 4) Legendary pickups (only the correct team can collect)
    const pickRSq = BOSS_TUNING.LEGENDARY_PICK_RADIUS * BOSS_TUNING.LEGENDARY_PICK_RADIUS;
    const surviving: ServerLegendary[] = [];
    for (const item of this.legendaries) {
      let picker: BossAttacker | null = null;
      for (const a of attackers) {
        if (!a.alive) continue;
        if (a.role !== item.team) continue;
        const dx = a.x - item.x;
        const dz = a.z - item.z;
        if (dx * dx + dz * dz <= pickRSq) { picker = a; break; }
      }
      if (picker) {
        events.pickups.push({ pickerId: picker.id, weapon: item.weapon, x: item.x, z: item.z });
      } else {
        surviving.push(item);
      }
    }
    this.legendaries = surviving;

    return events;
  }

  snapshot(): { bosses: BossSnap[]; legendaries: LegendarySnap[] } {
    return {
      bosses: this.bosses.map((b) => ({
        id: b.id,
        kind: b.kind,
        side: b.side,
        x: b.x,
        z: b.z,
        hp: Math.max(0, b.hp),
        hpMax: b.hpMax,
        targetId: b.targetId,
      })),
      legendaries: this.legendaries.map((l) => ({
        id: l.id,
        x: l.x,
        z: l.z,
        weapon: l.weapon,
        team: l.team,
      })),
    };
  }
}
